import React, {ReactElement, useEffect, useState} from "react";
import {Breathing} from "react-shimmer";
import classnames from "classnames";

import {post} from "../../api";
import {Loading} from "../loading";
import {EmptyComponent} from "../../types";
import {getPreSignedUrls, getVideoMetadata, postVideo, uploadVideo} from "../../data/media";

import styles from "./s3-video-upload.module.scss";

const maxVideoTitleLength = 100;

const getVideoInvalidReason = (size: number, duration: number, videoTitle: string): string | undefined => {
	const maxDuration = 140;
	const minDuration = 0.5;
	const maxFileSize = 512;
	const bytesToMb = (bytes: number) => Number(bytes / (1024 * 1024)).toFixed(2);
	const fileSize = Number(bytesToMb(size));

	if (duration > maxDuration || duration < minDuration) {
		return "The video you are uploading is not between 0.5s and 140s.";
	}

	if (fileSize > maxFileSize) {
		return "The video you are uploading is larger than 512MB.";
	}

	if (videoTitle.length > maxVideoTitleLength) {
		return "The title of the video you are uploading is larger than 100 characters.";
	}

	return "";
};

interface S3UploadComponentProps extends EmptyComponent {
	file: File;
	onSuccess: () => void;
	onError: (message: string) => void;
}

const VALID_FILE_TYPES = ["avi", "h264", "m4v", "mp4", "mpeg4", "mpg", "mov", "ogg", "quicktime", "webm"];
export const S3UploadComponent = ({
	className,
	file,
	onSuccess,
	onError,
}: S3UploadComponentProps): ReactElement => {
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (loading) {
			return;
		}

		try {
			const videoType = file.type.substring(6);

			if (!VALID_FILE_TYPES.includes(videoType.toLowerCase())) {
				throw new Error(
					`The video you are uploading is not a valid file type. Supported file types are: ${VALID_FILE_TYPES.join(
						", "
					)}. Your file type: ${videoType || "not found"}.`
				);
			}

			setLoading(true);

			getVideoMetadata(file)
				.then(async videoMeta => {
					const failReason = getVideoInvalidReason(file.size, videoMeta.duration, file.name);

					if (failReason) {
						return Promise.reject(failReason);
					}

					const preSignedUrl = await getPreSignedUrls(file, videoMeta.duration, file.size);
					const data = await uploadVideo(file, preSignedUrl);
					const {id} = await postVideo(data.s3Key, data.title);
					await post(`videos/${id}/process`);

					setLoading(false);
					onSuccess();
				})
				.catch(err =>
					onError(err?.toString() || `There was a problem uploading your video. Please try again later.`)
				)
				.finally(() => setLoading(false));
		} catch (err) {
			return onError(err?.toString() || `There was a problem uploading your video. Please try again later.`);
		}
	}, [file, loading, onError, onSuccess]);

	return (
		<div className={classnames(styles.container, className)}>
			<Breathing className={styles.shimmer} />
			{loading && <Loading position="absolute" />}
		</div>
	);
};
